import { Button, Stack, Typography, TypographyProps } from '@mui/material'
import { forwardRef } from 'react'
import { FieldError, FieldName, UseFormSetValue } from 'react-hook-form'
import {
  RadioButtonsGroup,
  RadioButtonsGroupProps,
  TextInputField,
  TextInputFieldProps,
} from 'utoniq-ui'

import { handleError, tLabel, useMessage, useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { Form, useForm, UseFormProps } from '@/core/form'

export type DeactivationConfirmFormProps = {
  onSubmit: (inputs: DeactivationFormValues) => Promise<void>
}

export type DeactivationFormValues = {
  reasonNumber: string
  otherReason?: string
  opinion?: string
}

const schema: UseFormProps['schema'] = o => ({
  reasonNumber: o.string().label(tLabel('deactivationConfirm.reasonNumber')),
  otherReason: o.string().when('reasonNumber', {
    is: '8',
    then: o.string().label(tLabel('deactivationConfirm.otherReason')),
    otherwise: o.string().allow('').optional(),
  }),
  opinion: o
    .string()
    .allow('')
    .optional()
    .label(tLabel('deactivationConfirm.opinion')),
})

export const DeactivationConfirmForm: Organism<DeactivationConfirmFormProps> =
  ({ onSubmit }) => {
    const { t } = useTranslation('deactivationConfirm')
    const { showError } = useMessage()
    const {
      handleSubmit,
      register,
      setValue,
      watch,
      setError,
      formState: { errors, isSubmitting, isValid },
    } = useForm<DeactivationFormValues>({
      schema,
      defaultValues: {
        reasonNumber: '1',
        otherReason: '',
        opinion: '',
      },
    })
    const watchReasonNumber = watch('reasonNumber')

    const listItems = [
      { value: '1', label: t('artistReason') },
      { value: '2', label: t('favoriteArtistReason') },
      { value: '3', label: t('itemReason') },
      { value: '4', label: t('useAppReason') },
      { value: '5', label: t('functionReason') },
      { value: '6', label: t('priceReason') },
      { value: '7', label: t('privacyReason') },
      { value: '8', label: t('otherReason') },
    ]

    const handleOnSubmit = handleSubmit(values =>
      onSubmit(values).catch(error => {
        handleError<DeactivationFormValues>(error, setError, showError)
      })
    )

    return (
      <Form onSubmit={handleOnSubmit} sx={{ px: 5 }}>
        <Stack sx={{ pb: watchReasonNumber === '8' ? 0 : 5 }}>
          <RadioButtonsCell
            name="reasonNumber"
            setValue={setValue}
            value={watchReasonNumber}
            listItems={listItems}
            error={errors?.reasonNumber}
          />
          {watchReasonNumber === '8' && (
            <TextInputCell
              type="text"
              error={errors?.otherReason}
              {...register('otherReason')}
            />
          )}
        </Stack>
        <TextInputCell
          type="textarea"
          textFieldProps={{
            multiline: true,
            rows: 10,
            InputProps: { sx: { px: 4, py: 3 } },
          }}
          titleProps={{
            children: (
              <Stack direction="row" alignItems="center">
                <Typography variant="h5">{t('opinionTitle')}</Typography>
                <Typography variant="caption">{t('any')}</Typography>
              </Stack>
            ),
          }}
          error={errors?.opinion}
          {...register('opinion')}
        />
        <ButtonCell
          loading={isSubmitting}
          disabled={isSubmitting || !isValid}
        />
      </Form>
    )
  }

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('deactivationConfirm')
    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

type ButtonCellProps = {
  loading: boolean
  disabled: boolean
}
const ButtonCell: Cell<ButtonCellProps> = ({ loading, disabled }) => {
  const { t } = useTranslation('deactivationConfirm')
  return (
    <CellFrame sx={{ textAlign: 'center', py: 3 }}>
      <Button
        type="submit"
        size="large"
        sx={{ width: 244 }}
        loading={loading}
        disabled={disabled}
      >
        {t('submit')}
      </Button>
    </CellFrame>
  )
}

type RadioButtonsCellProps = {
  name: FieldName<DeactivationFormValues>
  setValue: UseFormSetValue<DeactivationFormValues>
  value: string
  error?: FieldError
  titleProps?: TypographyProps
  listItems: RadioButtonsGroupProps['listItems']
}
const RadioButtonsCell = ({
  titleProps,
  error,
  setValue,
  value,
  name,
  listItems,
}: RadioButtonsCellProps) => {
  const { t } = useTranslation('deactivationConfirm')
  return (
    <CellFrame>
      <RadioButtonsGroup
        error={error}
        value={value}
        title={name}
        onChange={async value => {
          setValue(name, value, { shouldDirty: true, shouldValidate: true })
        }}
        listItems={listItems}
        titleProps={{
          variant: 'h5',
          color: 'text.primary',
          sx: { alignItems: 'center', display: 'flex' },
          ...titleProps,
        }}
        t={t}
      />
    </CellFrame>
  )
}
